import {
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@material-ui/core';
import {
  buildMenuTree,
  findAncestorKeys,
  getDescendantKeys,
  getItemLabel,
  hasActiveDescendant,
  isCurrentRoute,
  SIDEBAR_ICON_COLOR,
  SIDEBAR_TEXT_COLOR
} from 'lib/sidebarNavUtils';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { menuByModule } from 'routeConfig';
import { Link, useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import React, { useEffect, useMemo, useState } from 'react';
import useModulePermissions from 'hooks/useModulePermissions';
import { ExpandLessRounded, ExpandMoreRounded } from '@material-ui/icons';

const useStyles = makeStyles((theme) => ({
  menu: { paddingLeft: theme.spacing(1) },
  itemMenu: { paddingLeft: theme.spacing(2) },
  subItemMenu: { paddingLeft: theme.spacing(3) },
  selectedItem: {
    '&.Mui-selected': { backgroundColor: 'transparent' }
  },
  icon: {
    minWidth: 32,
    color: SIDEBAR_ICON_COLOR
  },
  activeIcon: { color: theme.palette.primary.main },
  subtleTitle: {
    fontWeight: 500,
    color: SIDEBAR_TEXT_COLOR
  },
  activeTitle: { color: theme.palette.primary.main },
  itemMenuFade: {
    animation: `$fadeInSlide 400ms ease-out`,
    position: 'relative',
    zIndex: 5,
    backgroundColor: theme.palette.common.white,
    borderRadius: 6,
    boxShadow: '0 0 0 rgba(0, 0, 0, 0)',
    padding: '4px 0',
    animationFillMode: 'forwards',
    transition: 'box-shadow 400ms ease, background-color 400ms ease'
  },
  '@keyframes fadeInSlide': {
    from: {
      opacity: 0,
      transform: 'translateY(-8px)',
      boxShadow: '0 0 0 rgba(0, 0, 0, 0)',
      backgroundColor: 'transparent'
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)',
      boxShadow: '0 6px 20px rgba(0, 0, 0, 0.3)',
      backgroundColor: theme.palette.common.white
    }
  },
  disabledItem: {
    opacity: 0.45,
    cursor: 'not-allowed'
  },
  emptyMessage: {
    paddingLeft: theme.spacing(4),
    opacity: 0.6,
    fontStyle: 'italic'
  }
}));

/* Menu lateral do módulo CRM */
const SidebarNav = ({ className, moduleCode }) => {
  const classes = useStyles();
  const location = useLocation();
  const { hasPerm, loading } = useModulePermissions(moduleCode);
  const [openMap, setOpenMap] = useState({});

  const rawMenu = useMemo(() => {
    return menuByModule[moduleCode] || [];
  }, [moduleCode]);

  const finalMenu = useMemo(() => {
    return buildMenuTree(rawMenu, hasPerm);
  }, [rawMenu, hasPerm]);

  // reseta os itens de menu na troca de rota
  useEffect(() => {
    setOpenMap({});
  }, [location.pathname]);

  const isOpen = (key) => !!openMap[key];

  const toggleOpen = (item) => {
    setOpenMap((prev) => {
      const currentlyOpen = !!prev[item.key];

      if (currentlyOpen) {
        // fecha item + descendentes
        const next = {
          ...prev,
          [item.key]: false
        };

        getDescendantKeys(item).forEach((childKey) => {
          next[childKey] = false;
        });

        return next;
      }

      // abre item + ancestrais
      const ancestorKeys = findAncestorKeys(finalMenu, item.key) || [];
      const next = {};

      ancestorKeys.forEach((ancestorKey) => {
        next[ancestorKey] = true;
      });

      next[item.key] = true;

      return next;
    });
  };

  const renderItems = (items, level = 0) => {
    return items.map((item) => {
      if (!item.visible) return null;

      const Icon = item.icon;
      const hasChildren = item.children && item.children.length > 0;
      const itemLabel = getItemLabel(item);
      const levelClass = level === 0 ? classes.menu : level === 1 ? classes.itemMenu : classes.subItemMenu;

      if (!hasChildren) {
        const isActive = isCurrentRoute(location.pathname, item.to);

        const commonProps = {
          key: item.key,
          className: clsx(
            levelClass,
            item.disabled && classes.disabledItem
          )
        };

        if (item.disabled) {
          return (
            <ListItem
              {...commonProps}
              button={false}
            >
              <ListItemIcon className={classes.icon}>
                {Icon ? <Icon /> : null}
              </ListItemIcon>

              <ListItemText
                primary={itemLabel}
                primaryTypographyProps={{ className: classes.subtleTitle }}
              />
            </ListItem>
          );
        }

        return (
          <ListItem
            {...commonProps}
            className={clsx(
              commonProps.className,
              classes.selectedItem
            )}
            button
            component={Link}
            to={item.to}
            selected={isActive}
          >
            <ListItemIcon className={clsx(
              classes.icon,
              isActive && classes.activeIcon
            )}>
              {Icon ? <Icon /> : null}
            </ListItemIcon>

            <ListItemText
              primary={itemLabel}
              primaryTypographyProps={{
                className: clsx(
                  classes.subtleTitle,
                  isActive && classes.activeTitle
                )
              }}
            />
          </ListItem>
        );
      }

      const enabledChildren = item.children.filter((child) => !child.disabled);
      const isActiveMenu = hasActiveDescendant(item, location.pathname);

      return (
        <div key={item.key}>
          <ListItem
            button
            className={levelClass}
            onClick={() => toggleOpen(item)}
          >
            <ListItemIcon className={clsx(
              classes.icon,
              isActiveMenu && classes.activeIcon
            )}>
              {Icon ? <Icon /> : null}
            </ListItemIcon>

            <ListItemText
              primary={itemLabel}
              primaryTypographyProps={{
                className: clsx(
                  classes.subtleTitle,
                  isActiveMenu && classes.activeTitle
                )
              }}
            />

            {isOpen(item.key) ? (
              <ExpandLessRounded
                className={clsx(isActiveMenu && classes.activeIcon)}
              />
            ) : (
              <ExpandMoreRounded
                className={clsx(isActiveMenu && classes.activeIcon)}
              />
            )}
          </ListItem>

          <Collapse
            in={isOpen(item.key)}
            timeout={400}
            unmountOnExit
          >
            <List
              component="div"
              disablePadding
              className={classes.itemMenuFade}
            >
              {enabledChildren.length > 0 ? (
                renderItems(item.children, level + 1)
              ) : (
                // fallback: nenhum filho liberado
                <ListItem className={levelClass}>
                  <ListItemText
                    primary="Nenhuma opção disponível"
                    primaryTypographyProps={{ className: classes.emptyMessage }}
                  />
                </ListItem>
              )}
            </List>
          </Collapse>
        </div>
      );
    });
  };

  return (
    <List className={className}>
      {loading ? (
        <ListItem>
          <ListItemText
            primary="Carregando permissões..."
          />
        </ListItem>
      ) : (
        renderItems(finalMenu)
      )}
    </List>
  );
};

SidebarNav.propTypes = {
  className: PropTypes.string,
  moduleCode: PropTypes.number.isRequired
};

export default SidebarNav;